import * as schema from "./schema"

type Translator = (s: string) => string

export function translateSchema(
    t: Translator,
    s: schema.Schema,
    prefix?: string,
): schema.Schema {
    const prefixedT = prefix ? prependKey(prefix, t) : t
    return {
        questions: s.questions.map(
            translateQuestion(prependKey("questions", prefixedT)),
        ),
        results: s.results.map(translateInfoBearer(prependKey("results", prefixedT))),
    }
}

const translateQuestion = (t: Translator) => (q: schema.Question) => {
    return {
        ...translateInfoBearer<schema.Question>(t)(q),
        options: q.options.map(
            translateInfoBearer<schema.Choice>(
                prependKey(`${q.id}.options`, t),
            ),
        ),
        subquestions: q.subquestions.map(translateSubquestion(t)),
    }
}

const translateSubquestion = (t: Translator) => (sq: schema.Subquestion) => {
    return {
        ...translateInfoBearer<schema.Subquestion>(t)(sq),
        options: sq.options.map(
            translateInfoBearer<schema.Choice>(
                prependKey(`${sq.id}.options`, t),
            ),
        ),
    }
}

const translateInfoBearer =
    <T extends schema.InfoBearer & { id: string }>(
        t: Translator,
    ): ((info: T) => T) =>
    (info) => {
        const localT = prependKey(info.id, t)

        const safeTranslate = (key: string, fallback?: string) => {
            const translated = localT(key)
            if (translated === `${info.id}.${key}`) {
                return fallback || ""
            }
            return translated
        }

        const infoEntries = {
            description: safeTranslate("description", info.info.description),
            detail: safeTranslate(
                "detail_markdown",
                info.info.detail,
            ) as schema.Markdown,
            hint: safeTranslate("hint", info.info.hint),
            tooltip: safeTranslate("tooltip", info.info.tooltip),
            context: safeTranslate("context", info.info.context),
            reference: safeTranslate("reference", info.info.reference),
            category: safeTranslate("category", info.info.category),
        }

        const cleanedInfo = Object.fromEntries(
            Object.entries(infoEntries).filter(
                ([, value]) => value !== "" && value != null,
            ),
        )

        const translatedTitle = safeTranslate("title", info.title || info.id)

        return {
            ...info,
            title: translatedTitle,
            info: cleanedInfo,
        }
    }

function prependKey(key: string, l10n: Translator): Translator {
    return (s: string) => l10n(`${key}.${s}`)
}
